import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import "./Travelers.css";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

/* ---------- tiny, dependency-free count-up ---------- */
function CountUp({ to = 0, duration = 900, prefix = "", suffix = "" }) {
  const [val, setVal] = useState(0);
  const startTs = useRef(null);
  const target = Number(to || 0);

  useEffect(() => {
    startTs.current = null;
    let raf = 0;

    const step = (ts) => {
      if (!startTs.current) startTs.current = ts;
      const p = Math.min(1, (ts - startTs.current) / duration);
      const eased = 1 - Math.pow(1 - p, 3); // easeOutCubic
      setVal(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };

    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);

  const formatted = Math.round(val).toLocaleString();
  return <>{prefix}{formatted}{suffix}</>;
}

// Status component reused for table and button styling
export function Status({ status, small = false }) {
  const clsBase = "badge";
  const clsColor = status === "Active" ? "green" : "red";
  const cls = `${clsBase} ${clsColor}`;
  const style = small
    ? { padding: "4px 8px", fontSize: 12, borderRadius: 12, display: "inline-block" }
    : { padding: "6px 10px", borderRadius: 14, display: "inline-block" };
  return (
    <span className={cls} style={{ ...style }}>
      {status}
    </span>
  );
}

export default function Travelers() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);

  const [stats, setStats] = useState({ totalTravelers: 0, activeTravelers: 0, inactiveTravelers: 0 });
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    dateFrom: '',
    dateTo: ''
  });
  const [statuses, setStatuses] = useState([]);

  const cards = useMemo(
    () => [
      { title: "Total Traveler Agents", value: stats.totalTravelers, sub: "Total Agents", icon: "🙍‍♂️", prefix: "+" },
      { title: "Active Traveler Agents", value: stats.activeTravelers, sub: "Active Agents", icon: "🟢", prefix: "+" },
      { title: "Inactive Traveler Agents", value: stats.inactiveTravelers, sub: "Inactive Agents", icon: "🔴", prefix: "+" },
    ],
    [stats]
  );

  useEffect(() => {
    async function fetchStatuses() {
      try {
        const res = await fetch(`${API}/statuses`);
        const data = await res.json();
        if (Array.isArray(data)) {
          const uniqueMap = new Map();
          data.forEach(s => {
            if (s && s.name && !uniqueMap.has(s.name.toLowerCase())) {
              uniqueMap.set(s.name.toLowerCase(), s);
            }
          });
          setStatuses(Array.from(uniqueMap.values()));
        }
      } catch (e) {
        console.error("Failed to fetch statuses", e);
      }
    }
    fetchStatuses();
  }, []);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        setLoading(true);
        setError("");

        const query = new URLSearchParams(filters).toString();

        const token = localStorage.getItem("adminToken");
        const headers = {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        };

        const [summaryRes, listRes] = await Promise.all([
          fetch(`${API}/admin/travelers/summary`, { headers }),
          fetch(`${API}/admin/travelers?${query}`, { headers }),
        ]);

        const summary = await summaryRes.json();
        const list = await listRes.json();

        if (!summaryRes.ok) throw new Error(summary?.message || "Failed summary");
        if (!listRes.ok) throw new Error(list?.message || "Failed list");

        if (!mounted) return;

        setStats({
          totalTravelers: Number(summary.totalTravelers) || 0,
          activeTravelers: Number(summary.activeTravelers) || 0,
          inactiveTravelers: Number(summary.inactiveTravelers) || 0,
        });

        setRows(
          (Array.isArray(list) ? list : []).map((r) => ({
            ...r,
            dateText: r.date ? new Date(r.date).toLocaleString() : "—",
            is_active: r.is_active === true || r.is_active === 4,
            status: r.status ? String(r.status) : (r.is_active === 4 || r.is_active === true ? "Active" : "Inactive"),
          }))
        );
      } catch (e) {
        if (mounted) setError(e.message || "Load failed");
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();
    return () => {
      mounted = false;
    };
  }, [filters]);

  async function toggleStatus(travelerId) {
    try {
      // optimistic UI
      setRows((prev) =>
        prev.map((r) =>
          r.travelerId === travelerId ? { ...r, status: r.status === "Active" ? "Inactive" : "Active" } : r
        )
      );

      const res = await fetch(`${API}/admin/travelers/${travelerId}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to update status.");

      // precise update + refresh cards
      setRows((prev) =>
        prev.map((r) =>
          r.travelerId === travelerId
            ? {
              ...r,
              status: data.row.status,
              fullName: data.row.fullName,
              phoneNumber: data.row.phoneNumber,
              email: data.row.email,
              date: data.row.date,
              dateText: data.row.date ? new Date(data.row.date).toLocaleString() : "—",
            }
            : r
        )
      );
      setStats({
        totalTravelers: Number(data.summary.totalTravelers) || 0,
        activeTravelers: Number(data.summary.activeTravelers) || 0,
        inactiveTravelers: Number(data.summary.inactiveTravelers) || 0,
      });
    } catch (e) {
      console.error(e);
      alert(e.message || "Failed to update status.");
      // Optional: reload list for consistency
      try {
        const listRes = await fetch(`${API}/admin/travelers`);
        const list = await listRes.json();
        if (listRes.ok) {
          setRows(
            (Array.isArray(list) ? list : []).map((r) => ({
              ...r,
              dateText: r.date ? new Date(r.date).toLocaleString() : "—",
              is_active: r.is_active === true || r.is_active === 4,
              status: r.status ? String(r.status) : (r.is_active === 4 || r.is_active === true ? "Active" : "Inactive"),
              userExists: r.userExists === true || Number(r.user_exists || r.userExists || 0) === 1,
            }))
          );
        }
      } catch { }
    }
  }

  async function toggleActive(travelerId) {
    try {
      setRows((prev) =>
        prev.map((r) =>
          r.travelerId === travelerId ? { ...r, userExists: !r.userExists } : r
        )
      );

      const res = await fetch(`${API}/admin/travelers/${travelerId}/active`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to update active flag.");

      setRows((prev) =>
        prev.map((r) =>
          r.travelerId === travelerId
            ? { ...r, userExists: data.row.userExists }
            : r
        )
      );
      setStats({
        totalTravelers: Number(data.summary.totalTravelers) || 0,
        activeTravelers: Number(data.summary.activeTravelers) || 0,
        inactiveTravelers: Number(data.summary.inactiveTravelers) || 0,
      });
    } catch (e) {
      console.error(e);
      alert(e.message || "Failed to update active flag.");
    }
  }

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: '',
      status: '',
      dateFrom: '',
      dateTo: ''
    });
  };

  if (error) {
    return (
      <div className="owners-page">
        <div className="owners-head">
          <div>
            <h2 className="owners-title">Traveler's</h2>
            <p className="muted">An error occurred</p>
          </div>
        </div>
        <section className="card table-card" style={{ padding: 24, color: "crimson" }}>
          {error}
        </section>
      </div>
    );
  }

  return (
    <div className="owners-page">
      {/* Page header */}
      <div className="owners-head">
        <div>
          <h2 className="owners-title">Traveler Agents</h2>
          <p className="muted">Manage all Traveler Agents</p>
        </div>
      </div>

      {/* Stat cards with animated numbers */}
      <section className="owners-cards">
        {cards.map((s, i) => (
          <div key={i} className="card stat">
            <div className="stat-title">{s.title}</div>
            <div className="stat-value">
              <CountUp to={s.value} prefix={s.prefix || ""} />
            </div>
            <div className="stat-sub">{s.sub}</div>
            <div className="lock" aria-hidden>{s.icon}</div>
          </div>
        ))}
      </section>

      <div className="filters">
        <input type="text" name="search" placeholder="Search by ID, Name, Phone" value={filters.search} onChange={handleFilterChange} />
        <select name="status" value={filters.status} onChange={handleFilterChange} className={`input ${filters.status ? "filter-selected" : "filter-muted"}`}>
          <option value="">All Statuses</option>
          {statuses.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
        </select>
        <input type="date" name="dateFrom" value={filters.dateFrom} onChange={handleFilterChange} className={`input ${filters.dateFrom ? "filter-selected" : "filter-muted"}`} />
        <input type="date" name="dateTo" value={filters.dateTo} onChange={handleFilterChange} className={`input ${filters.dateTo ? "filter-selected" : "filter-muted"}`} />
        <button onClick={handleClearFilters}>Clear All</button>
      </div>

      <h3 className="owners-subtitle">All Traveler's</h3>

      {/* Table */}
      <section className={`card table-card ${loading ? 'loading' : ''}`}>
        <table className="table">
          <thead>
            <tr>
              <th style={{ width: 34 }}>
                <input type="checkbox" aria-label="select all" />
              </th>
              <th>Registered ID</th>
              <th>Full Name</th>
              <th>Phone</th>
              <th>Email</th>
              <th>Date of Registered</th>
              <th>Status</th>
              <th>Details</th>
              <th>Remove Account</th>
              <th style={{ width: 140 }}>Hold Account</th>
              <th style={{ width: 40 }}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.travelerId}>
                <td><input type="checkbox" aria-label={`select ${r.regId}`} /></td>
                <td><span className="id-badge">{r.regId}</span></td>
                <td>{r.fullName}</td>
                <td className="muted">{r.phoneNumber}</td>
                <td className="muted">{r.email}</td>
                <td className="muted nowrap">{r.dateText}</td>
                {/* Status column uses Status component */}
                <td>
                  <Status status={r.status} />
                </td>
                <td>
                  <button 
                    className="badge" 
                    style={{background: '#f8fafc', border: '1px solid #e2e8f0', color: '#64748b'}}
                    onClick={() => navigate(`/admin/travelers/${r.travelerId}`)}
                  >
                    View Details
                  </button>
                </td>
                <td>
                  <button
                    className="badge"
                    onClick={() => toggleActive(r.travelerId)}
                    title={r.userExists ? "Remove account (prevent login)" : "Admit account (restore login)"}
                    style={{
                      color: r.userExists ? "#f97316" : "#047857",
                      background: r.userExists ? "#fff7ed" : "#ecfdf5",
                    }}
                  >
                    {r.userExists ? "Remove" : "Admit"}
                  </button>
                </td>
                <td>
                  {/* Remove / Admit button: label + Status-style small badge */}
                  <button
                    className="badge"
                    onClick={() => toggleStatus(r.travelerId)}
                    title={r.status === "Active" ? "Hold (preventing listing)" : "Unhold (allow listing)"}
                    style={{
                      color: r.status === "Active" ? "#f97316" : "#047857",
                      background: r.status === "Active" ? "#fff7ed" : "#ecfdf5",
                    }}
                  >
                    {r.status === "Active" ? "Hold" : "Unhold"}

                  </button>
                </td>
                <td className="dots">⋯</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}

// export function Status({ status, onClick }) {
//   const cls = status === "Active" ? "badge green" : "badge red";
//   return (
//     <button className={`${cls} as-button`} onClick={onClick} title="Click to toggle status">
//       {status}
//     </button>
//   );
// }